import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, IconButton, MenuItem, Alert,
  TablePagination
} from '@mui/material';
import { Add, Edit, Settings } from '@mui/icons-material';

const SupplierProducts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);
  const [formData, setFormData] = useState({
    name: '', nameAr: '', description: '', descriptionAr: '',
    category: '', basePrice: '', moq: '1', leadTime: '14', supplierProductCode: ''
  });

  useEffect(() => {
    loadProducts();
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products/supplier/my-products', {
        params: {
          page: page + 1,
          limit: rowsPerPage
        }
      });
      setProducts(data.data || []);
      setTotalProducts(data.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data.data || []);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = {
        name: formData.name,
        nameAr: formData.nameAr,
        description: formData.description,
        descriptionAr: formData.descriptionAr,
        supplierProductCode: formData.supplierProductCode,
        pricing: { basePrice: Number(formData.basePrice) || 0, moq: Number(formData.moq) || 1 },
        shipping: { leadTime: Number(formData.leadTime) || 14 }
      };
      if (formData.category) payload.category = formData.category;

      if (editProduct) {
        await api.put(`/products/${editProduct._id}`, payload);
      } else {
        await api.post('/products', payload);
      }
      setDialogOpen(false);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product');
    }
  };

  const openAdd = () => {
    setEditProduct(null);
    setError('');
    setFormData({ name: '', nameAr: '', description: '', descriptionAr: '', category: '', basePrice: '', moq: '1', leadTime: '14', supplierProductCode: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setError('');
    setFormData({
      name: p.name, nameAr: p.nameAr || '', description: p.description || '',
      descriptionAr: p.descriptionAr || '', category: p.category?._id || '',
      basePrice: p.pricing?.basePrice?.toString() || '', moq: p.pricing?.moq?.toString() || '1',
      leadTime: p.shipping?.leadTime?.toString() || '14',
      supplierProductCode: p.supplierProductCode || ''
    });
    setDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statusColor = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'error';
    if (status === 'draft') return 'default';
    return 'warning';
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>{t('my_products')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>{t('add_product')}</Button>
      </Box>

      {products.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No products yet</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Click "Add Product" to create your first product listing
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ mt: 2 }}>Add Product</Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>OSESG Code</TableCell>
                <TableCell>Your Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>MOQ</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p._id} hover>
                  <TableCell><Chip label={p.osesgCode} size="small" color="primary" /></TableCell>
                  <TableCell>{p.supplierProductCode || '-'}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.category?.name || '-'}</TableCell>
                  <TableCell>${p.pricing?.basePrice?.toLocaleString() || '0'}</TableCell>
                  <TableCell>{p.pricing?.moq}</TableCell>
                  <TableCell><Chip label={p.status} size="small" color={statusColor(p.status)} /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(p)} title="Edit"><Edit /></IconButton>
                    <IconButton size="small" onClick={() => navigate(`/supplier/products/${p._id}`)} title="Settings">
                      <Settings />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalProducts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Products per page:"
          />
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editProduct ? 'Edit Product' : t('add_product')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Product Name" value={formData.name} required
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="اسم المنتج (Arabic)" value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label="Category" value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                <MenuItem value="">-- Select Category --</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Your Catalog Code (Internal)" value={formData.supplierProductCode}
                onChange={(e) => setFormData({ ...formData, supplierProductCode: e.target.value })}
                helperText="Your internal product reference number" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="الوصف (Arabic)" value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Base Price ($)" type="number" value={formData.basePrice}
                onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="MOQ" type="number" value={formData.moq}
                onChange={(e) => setFormData({ ...formData, moq: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Lead Time (days)" type="number" value={formData.leadTime}
                onChange={(e) => setFormData({ ...formData, leadTime: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierProducts;
